import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  HelpCircle, 
  Mail, 
  MessageCircle, 
  Clock, 
  Users, 
  Shield, 
  FileText,
  Home,
  BookOpen
} from 'lucide-react';
import { useSidebarLayout } from '@/hooks/useSidebarLayout';

const HelpPage: React.FC = () => {
  const { getPageHeaderClass } = useSidebarLayout();
  
  const handleContactAdmin = () => {
    const subject = encodeURIComponent('Tectonix Procurement - Support Request');
    const body = encodeURIComponent(`Hello,

I need assistance with the Tectonix Procurement application.

Issue Description:
[Please describe your issue here]

Additional Information:
- User: [Your name/email]
- Date: ${new Date().toLocaleDateString()}
- Time: ${new Date().toLocaleTimeString()}

Thank you for your help.

Best regards,
[Your name]`);

    window.open(`mailto:admin@moatcity.com?subject=${subject}&body=${body}`, '_blank');
  };

  const handleOpenUserGuide = () => {
    const targetUrl = 'https://www.tectonixai.com/help/tectonix-procurement';
    window.open(targetUrl, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Page Header - single line */}
      <div className={getPageHeaderClass()}>
        <div className="h-full flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <HelpCircle className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">Help & Support</h1>
          </div>
        </div>
      </div>
      
      {/* Main Content with top padding to account for both headers (64px global + 64px page = 128px = pt-32) */}
      <main className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 max-w-full mx-auto">
        {/* Add gap after the header */}
        <div className="mb-8"></div>
        
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Quick Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Need Immediate Help?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                If you're experiencing issues or need assistance with the application, 
                our dedicated app admin team is here to help.
              </p>
              <Button 
                onClick={handleContactAdmin}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Mail className="h-4 w-4 mr-2" />
                Contact App Admin
              </Button>
              <p className="text-sm text-gray-500 mt-2 text-center">
                admin@moatcity.com
              </p>
              <Button
                onClick={handleOpenUserGuide}
                variant="outline"
                className="w-full mt-4"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Open User Guide
              </Button>
            </CardContent>
          </Card>

          {/* Common Issues */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Common Issues & Solutions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900">Can't see my projects?</h4>
                <p className="text-gray-600 text-sm">
                  Make sure you're logged in with the correct account. Projects are filtered by your access permissions.
                </p>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-gray-900">Date sorting not working?</h4>
                <p className="text-gray-600 text-sm">
                  We've recently updated the date sorting to use real creation dates from ACC API. Try refreshing the page.
                </p>
              </div>
              
              <div className="border-l-4 border-orange-500 pl-4">
                <h4 className="font-semibold text-gray-900">Slow loading times?</h4>
                <p className="text-gray-600 text-sm">
                  The application fetches data from Autodesk ACC API. Large project lists may take a few seconds to load.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Features Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Application Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Users className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Project Management</h4>
                    <p className="text-gray-600 text-sm">View and manage all your ACC projects</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Clock className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Real-time Data</h4>
                    <p className="text-gray-600 text-sm">Live data from Autodesk Construction Cloud</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <FileText className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Document Access</h4>
                    <p className="text-gray-600 text-sm">Access project documents and files</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 rounded-full">
                    <Shield className="h-4 w-4 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Secure Access</h4>
                    <p className="text-gray-600 text-sm">Role-based permissions and security</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="text-center text-gray-500 text-sm">
            <p>Tectonix Procurement - Built for efficient project management</p>
            <p className="mt-1">
              Need help? Contact us at{' '}
              <button 
                onClick={handleContactAdmin}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                admin@moatcity.com
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpPage;
